import axios, { AxiosResponse } from 'axios'
import qs from 'qs'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/store/modules/user'
import cache from '@/utils/cache'
import { i18n } from '@/i18n'
import { ElMessageBox } from 'element-plus/es'

// axios实例
const service = axios.create({
	baseURL: '/api',
	timeout: 300000,
	headers: { 'Content-Type': 'application/json;charset=UTF-8' }
})

// 是否已显示登录超时弹窗
let isShowLoginTimeout = false

// 请求拦截器
service.interceptors.request.use(
	(config: any) => {
		const userStore = useUserStore()
		if (userStore?.token) {
			config.headers.Authorization = userStore.token
		}

		config.headers['Accept-Language'] = cache.getLanguage()
		config.headers['lang'] = cache.getLanguage().replace('-', '_')
		config.headers['tenantId'] = cache.getTenantId()

		// 追加时间戳，防止GET请求缓存
		if (config.method?.toUpperCase() === 'GET') {
			config.params = { ...config.params, t: new Date().getTime() }
		}

		// 如果是FormData，删除Content-Type让浏览器自动设置
		if (config.data instanceof FormData) {
			delete config.headers['Content-Type']
		} else if (Object.values(config.headers).includes('application/x-www-form-urlencoded')) {
			config.data = qs.stringify(config.data)
		}

		return config
	},
	error => {
		return Promise.reject(error)
	}
)

// 是否刷新
let isRefreshToken = false
// 重试请求
let requests: any[] = []

// 刷新token
const getRefreshToken = (refreshToken: string) => {
	return service.post('/sys/auth/token?refreshToken=' + refreshToken)
}

// 响应拦截器
service.interceptors.response.use(
	async (response: AxiosResponse<any>) => {
		const userStore = useUserStore()
		if (response.status !== 200) {
			return Promise.reject(new Error(response.statusText || 'Error'))
		}

		const res = response.data
		if (Object.prototype.toString.call(res) === '[object Blob]') {
			return response
		}

		// 如果返回的是数组，直接返回
		if (Array.isArray(res)) {
			return res
		}

		// 响应成功
		if (res.code === 0) {
			return res
		}

		// refreshToken失效，跳转到登录页
		if (res.code === 400) {
			return handleAuthorized()
		}

		// 没有权限，如：未登录、token过期
		if (res.code === 401) {
			const config = response.config
			if (!isRefreshToken) {
				isRefreshToken = true
				// 不存在 refreshToken，重新登录
				const refreshToken = cache.getRefreshToken()
				if (!refreshToken) {
					return handleAuthorized()
				}

				try {
					const { data } = await getRefreshToken(refreshToken)
					// 设置新 token
					userStore.setToken(data.access_token)
					config.headers!.Authorization = data.access_token
					requests.forEach((cb: any) => {
						cb()
					})
					requests = []
					return service(config)
				} catch (e) {
					// 刷新失败
					requests.forEach((cb: any) => {
						cb()
					})
					return handleAuthorized()
				} finally {
					requests = []
					isRefreshToken = false
				}
			} else {
				// 多个请求的情况
				return new Promise(resolve => {
					requests.push(() => {
						config.headers!.Authorization = userStore.token
						resolve(service(config))
					})
				})
			}
		}

		// 错误提示
		ElMessage.error(res.msg)

		return Promise.reject(new Error(res.msg || 'Error'))
	},
	error => {
		ElMessage.error(error.message)
		return Promise.reject(error)
	}
)

const handleAuthorized = () => {
	cache.removeUserInfo()
	if (isShowLoginTimeout) {
		return Promise.reject(i18n.global.t('framework.loginTimeout.message'))
	}
	isShowLoginTimeout = true
	ElMessageBox.confirm(i18n.global.t('framework.loginTimeout.message'), i18n.global.t('framework.common.tip'), {
		showCancelButton: false,
		closeOnClickModal: false,
		showClose: false,
		confirmButtonText: i18n.global.t('framework.loginTimeout.relogin'),
		type: 'warning'
	})
		.then(() => {
			cache.clearUserData()
			const userStore = useUserStore()
			userStore?.setToken('')
			userStore?.setRefreshToken('')
			location.reload()
			isShowLoginTimeout = false
		})
		.catch(() => {
			isShowLoginTimeout = false
		})

	return Promise.reject(i18n.global.t('framework.loginTimeout.message'))
}

// 导出 axios 实例
export default service
